import { useNavigate } from 'react-router-dom'
import { Shield, Lock, KeyRound, AlertTriangle, CheckCircle, ExternalLink, FileText, Eye, Activity } from 'lucide-react'
import Header from '../components/Header'
import { useI18n } from '../i18n/useI18n'

export default function SecurityPolicy() {
  const navigate = useNavigate()
  const { t } = useI18n()

  return (
    <div className="flex min-h-screen flex-col bg-warm-white safe-top safe-bottom">
      <Header title={t('security.title')} onBack={() => navigate(-1)} />

      <div className="flex-1 px-4 pt-4 pb-8 space-y-4">
        {/* Wallet Architecture Section */}
        <div className="rounded-xl bg-white p-5 shadow-card">
          <div className="flex items-center gap-2 mb-4">
            <Shield size={20} className="text-navy" />
            <h2 className="font-display text-[15px] font-bold text-text-primary">
              {t('security.walletArchitecture')}
            </h2>
          </div>

          <div className="space-y-4">
            {/* MPC-TSS */}
            <div className="flex gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-navy/5">
                <KeyRound size={18} className="text-navy" />
              </div>
              <div>
                <p className="font-display text-sm font-semibold text-text-primary">
                  {t('security.mpcTitle')}
                </p>
                <p className="mt-0.5 font-body text-xs text-text-secondary leading-relaxed">
                  {t('security.mpcDesc')}
                </p>
              </div>
            </div>

            {/* Non-Custodial */}
            <div className="flex gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-matcha/10">
                <Lock size={18} className="text-matcha" />
              </div>
              <div>
                <p className="font-display text-sm font-semibold text-text-primary">
                  {t('security.nonCustodial')}
                </p>
                <p className="mt-0.5 font-body text-xs text-text-secondary leading-relaxed">
                  {t('security.nonCustodialDesc')}
                </p>
              </div>
            </div>

            {/* Social Recovery */}
            <div className="flex gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gold/10">
                <ExternalLink size={18} className="text-gold" />
              </div>
              <div>
                <p className="font-display text-sm font-semibold text-text-primary">
                  {t('security.socialRecovery')}
                </p>
                <p className="mt-0.5 font-body text-xs text-text-secondary leading-relaxed">
                  {t('security.socialRecoveryDesc')}
                </p>
              </div>
            </div>
          </div>

          {/* Powered by badge */}
          <div className="mt-4 flex items-center justify-center gap-2 rounded-lg bg-navy/5 py-2">
            <Shield size={14} className="text-navy/60" />
            <span className="font-body text-[11px] text-navy/60 tracking-wide">
              {t('security.poweredBy')}
            </span>
          </div>
        </div>

        {/* Data Protection Section */}
        <div className="rounded-xl bg-white p-5 shadow-card">
          <div className="flex items-center gap-2 mb-4">
            <Lock size={20} className="text-navy" />
            <h2 className="font-display text-[15px] font-bold text-text-primary">
              {t('security.dataProtection')}
            </h2>
          </div>

          <div className="space-y-2.5">
            {[
              { icon: CheckCircle, text: t('security.tls') },
              { icon: CheckCircle, text: t('security.pciDss') },
              { icon: CheckCircle, text: t('security.noCardStorage') },
              { icon: CheckCircle, text: t('security.keyInaccessible') },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <item.icon size={16} className="shrink-0 text-matcha" />
                <span className="font-body text-sm text-text-primary">{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Compliance Section */}
        <div className="rounded-xl bg-white p-5 shadow-card">
          <div className="flex items-center gap-2 mb-4">
            <FileText size={20} className="text-navy" />
            <h2 className="font-display text-[15px] font-bold text-text-primary">
              {t('compliance.title')}
            </h2>
          </div>

          <div className="space-y-3">
            {/* KYC */}
            <div className="rounded-lg border border-gray-100 p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Eye size={16} className="text-text-tertiary" />
                  <span className="font-display text-sm font-semibold text-text-primary">
                    {t('compliance.kycStatus')}
                  </span>
                </div>
                <span className="rounded-full bg-gold/10 px-2.5 py-0.5 text-[10px] font-bold text-gold">
                  {t('compliance.kycPending')}
                </span>
              </div>
              <p className="mt-1.5 font-body text-xs text-text-secondary leading-relaxed">
                {t('compliance.kycDesc')}
              </p>
            </div>

            {/* AML/CFT */}
            <div className="rounded-lg border border-gray-100 p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield size={16} className="text-text-tertiary" />
                  <span className="font-display text-sm font-semibold text-text-primary">
                    {t('compliance.amlStatus')}
                  </span>
                </div>
                <span className="rounded-full bg-matcha/10 px-2.5 py-0.5 text-[10px] font-bold text-matcha">
                  {t('compliance.amlCompliant')}
                </span>
              </div>
              <p className="mt-1.5 font-body text-xs text-text-secondary leading-relaxed">
                {t('compliance.amlDesc')}
              </p>
            </div>

            {/* Daily Limit */}
            <div className="rounded-lg border border-gray-100 p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle size={16} className="text-text-tertiary" />
                  <span className="font-display text-sm font-semibold text-text-primary">
                    {t('compliance.dailyLimit')}
                  </span>
                </div>
                <span className="font-display text-sm font-bold text-navy">
                  {t('compliance.dailyLimitValue')}
                </span>
              </div>
            </div>

            {/* Tx Monitoring */}
            <div className="rounded-lg border border-gray-100 p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Activity size={16} className="text-text-tertiary" />
                  <span className="font-display text-sm font-semibold text-text-primary">
                    {t('compliance.txMonitoring')}
                  </span>
                </div>
                <span className="rounded-full bg-matcha/10 px-2.5 py-0.5 text-[10px] font-bold text-matcha">
                  {t('compliance.amlCompliant')}
                </span>
              </div>
              <p className="mt-1.5 font-body text-xs text-text-secondary leading-relaxed">
                {t('compliance.txMonitoringDesc')}
              </p>
            </div>

            {/* Funds Act */}
            <div className="rounded-lg border border-gray-100 p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText size={16} className="text-text-tertiary" />
                  <span className="font-display text-sm font-semibold text-text-primary">
                    {t('compliance.fundsAct')}
                  </span>
                </div>
                <span className="rounded-full bg-matcha/10 px-2.5 py-0.5 text-[10px] font-bold text-matcha">
                  {t('compliance.amlCompliant')}
                </span>
              </div>
              <p className="mt-1.5 font-body text-xs text-text-secondary leading-relaxed">
                {t('compliance.fundsActDesc')}
              </p>
            </div>
          </div>
        </div>

        {/* Demo note */}
        <div className="rounded-lg bg-gold/5 border border-gold/20 px-4 py-3">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5 text-gold" />
            <p className="font-body text-xs text-text-secondary leading-relaxed">
              {t('compliance.demoNote')}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
